"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type Asset = { filename: string; alt?: string };

type ClinicLocatorBlok = SbBlokData & {
  title?: string;
  body?: string;
  bg_image?: Asset;
  button_label?: string;
  button_link?: StoryblokLink;
  bg_color?: string;
};

export default function ClinicLocator({ blok }: { blok: ClinicLocatorBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const { href, target } = resolveLink(blok.button_link, region);
  const { bg, onDark } = resolveBg(blok.bg_color, "midnight");
  const showButton = Boolean(blok.button_label && href !== "#");
  const titleColor = onDark ? "text-purple-01" : "text-purple-04";

  return (
    <section
      {...storyblokEditable(blok)}
      className={`${bg} ${onDark ? "text-white" : "text-black"}`}
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-6 px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(2.5rem,5vw,4.5rem)] pb-[clamp(3.75rem,6vw,4.5rem)] [@media(min-width:760px)]:flex-row [@media(min-width:760px)]:items-center [@media(min-width:760px)]:gap-[clamp(2rem,5vw,4.875rem)] lg:py-[71px]">
        <div className="flex flex-col gap-5 [@media(min-width:760px)]:flex-1 lg:w-[468px] lg:shrink-0 lg:flex-initial">
          {blok.title && (
            <h2
              className={`text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] ${titleColor}`}
            >
              {blok.title}
            </h2>
          )}
          {blok.body && (
            <p className="text-body leading-[20px] lg:leading-normal">
              {blok.body}
            </p>
          )}
          {showButton && (
            <a
              href={href}
              {...(target ? { target } : {})}
              className="inline-flex h-12 w-full items-center justify-center rounded-lg border border-white px-5 text-body font-bold text-white transition-colors hover:bg-white/10 [@media(min-width:760px)]:w-fit [@media(min-width:760px)]:px-7 lg:h-14"
            >
              {blok.button_label}
            </a>
          )}
        </div>

        {blok.bg_image?.filename && (
          <div className="order-first [@media(min-width:760px)]:order-0 [@media(min-width:760px)]:min-w-0 [@media(min-width:760px)]:flex-1">
            <Image
              src={blok.bg_image.filename}
              alt={blok.bg_image.alt || ""}
              width={1256}
              height={658}
              sizes="(max-width: 760px) 100vw, (max-width: 1115px) 50vw, 628px"
              className="aspect-320/329 w-full rounded-[20px] object-cover [@media(min-width:760px)]:aspect-628/329"
            />
          </div>
        )}
      </div>
    </section>
  );
}
